import { PurchaseOrderReference } from './PurchaseOrderStore';
import { TaskReference } from './TaskStore';
import { VatRateReference } from './VatRateStore';
import {
  ProductReference,
  SupplierItemReference,
  ICostPrice,
  IProduct,
  ISupplierItem,
  CostPrice,
  Unit,
} from './products/ProductStore';
import { AgreementReference } from './AgreementStore';
import { SaleReference } from './SaleStore';
import {
  Instance,
  types,
  SnapshotIn,
  SnapshotOut,
  IAnyModelType,
  getRoot,
  IReferenceType,
  IMaybe,
} from 'mobx-state-tree';
import { RootModelType } from './Root';
import { modelWithCRUD } from './utils/modelWithCRUD';

export const computeContributionMarginRatio = (
  revenue: number,
  cost: number
) => {
  if (revenue === 0) {
    return -1;
  }
  return (revenue - cost) / revenue;
};

export const SaleItem = types
  .model('SaleItem', {
    id: types.union(types.identifierNumber),
    position: types.optional(types.number, 1),
    parent_item: types.maybeNull(
      types.late((): IReferenceType<IAnyModelType> => SaleItemReference)
    ),
    child_items: types.maybeNull(
      types.array(
        types.late((): IReferenceType<IAnyModelType> => SaleItemReference)
      )
    ),
    sale: types.maybeNull(types.late(() => SaleReference)),
    agreement: types.maybeNull(
      types.late(
        (): IMaybe<IReferenceType<IAnyModelType>> => AgreementReference
      )
    ),
    ordering: types.optional(types.number, 0),

    is_heading: types.optional(types.boolean, false),
    is_hidden: types.optional(types.boolean, false),

    vat_rate: types.maybeNull(types.late(() => VatRateReference)),

    product: types.maybeNull(types.late(() => ProductReference)),
    supplier_item: types.maybeNull(types.late(() => SupplierItemReference)),

    related_purchase_orders: types.array(
      types.late(() => PurchaseOrderReference)
    ),
    task: types.maybeNull(types.late(() => TaskReference)),

    title: types.maybeNull(types.string),
    quantity: types.number,
    unit: types.enumeration<Unit>(Object.values(Unit)),
    unit_price: types.number,
    unit_price_currency: types.string,
    discount: types.maybeNull(types.number),

    quantity_completed: types.optional(types.number, 0),
    quantity_reserved: types.optional(types.number, 0),
    quantity_missing: types.optional(types.number, 0),

    own_cost_price: types.maybeNull(types.number),
    own_cost_price_currency: types.maybeNull(types.string),

    cost_price: CostPrice,
    contribution_margin: CostPrice,
    contribution_margin_ratio: CostPrice,
  })
  .views((self) => ({
    get computedCostPrice(): ICostPrice | undefined {
      const currency = self.sale
        ? self.sale.currency
        : self.agreement
        ? self.agreement.currency
        : 'DKK';

      if (self.supplier_item) {
        return {
          precise: self.supplier_item.cost_price * self.quantity!,
          precise_currency: currency,
        } as ICostPrice;
      }
      if (self.product) {
        const productCostPrice = (self.product as IProduct).cost_price;
        return !productCostPrice
          ? undefined
          : productCostPrice.precise || productCostPrice.precise === 0
          ? ({
              precise: productCostPrice.precise * self.quantity,
              precise_currency: currency,
            } as ICostPrice)
          : ({
              minimum: productCostPrice.minimum! * self.quantity,
              maximum: productCostPrice.maximum! * self.quantity,
              minimum_currency: currency,
              maximum_currency: currency,
            } as ICostPrice);
      }
      if (self.supplier_item === undefined || self.product === undefined) {
        return undefined;
      }
      return {
        precise: self.own_cost_price! * self.quantity,
        precise_currency: currency,
      } as ICostPrice;
    },
    get computedPrice(): number {
      return (
        (self.unit_price || 0) * self.quantity * (1 - (self.discount || 0))
      );
    },
  }))
  .actions((self) => ({
    resetToProduct() {
      if (
        !self.supplier_item ||
        !(self.supplier_item! as ISupplierItem).product
      )
        return;
      const product = (self.product || self.supplier_item!.product) as IProduct;
      self.title = product.name;
      self.unit_price = product.retail_price;
      self.discount = 0;
    },
  }))
  .actions((self) => ({
    setTitle(title: string) {
      self.title = title;
    },
    setHidden(flag: boolean) {
      self.is_hidden = flag;
    },
    setUnitPrice(unitPrice: number) {
      self.unit_price = unitPrice;
    },
    setPrice(price: number) {
      // Compute new unit price, keeping discount.
      if (self.quantity <= 0) self.quantity = 1;
      self.unit_price = price / (self.quantity * (1 - (self.discount || 0)));
      return self.unit_price;
    },
    setQuantity(qty: number) {
      if (qty < 0) self.quantity = 0;
      else self.quantity = qty;
    },
    setDiscount(discount: number) {
      self.discount = 0;
      if (discount > 1) {
        self.discount = 1;
        return;
      }
      if (discount < 0) {
        self.discount = 0;
        return;
      }
      self.discount = discount;
    },
    setCostPrice(costPrice: number | null, currency?: string) {
      if (costPrice === null) {
        self.own_cost_price = null;
        return;
      }
      self.own_cost_price = costPrice / Math.abs(self.quantity);
      self.own_cost_price_currency = currency || self.unit_price_currency;
    },
    setProduct(product: IProduct) {
      // If the product has a default supplier, we should use that.
      if (product.default_supplier_item) {
        self.supplier_item = product.default_supplier_item;
        self.product = null;
      } else {
        self.product = product;
        self.supplier_item = null;
      }
      self.resetToProduct();
    },
    setSupplierItem(item: ISupplierItem | null) {
      self.supplier_item = item;
      self.resetToProduct();
    },
  }))
  .views((self) => ({
    get contributionMarginRatio(): ICostPrice | undefined {
      const costPrice = self.computedCostPrice;
      if (!costPrice) {
        return undefined;
      }

      if (costPrice.precise) {
        return {
          precise: computeContributionMarginRatio(
            self.computedPrice,
            costPrice.precise
          ),
          precise_currency: 'DKK',
        } as ICostPrice;
      }
      // Notice we swapped min and max - this is intended to get the correct
      if (costPrice.minimum && costPrice.maximum) {
        return {
          minimum: computeContributionMarginRatio(
            self.computedPrice,
            costPrice.maximum
          ),
          maximum: computeContributionMarginRatio(
            self.computedPrice,
            costPrice.minimum
          ),
          minimum_currency: 'DKK',
          maximum_currency: 'DKK',
        } as ICostPrice;
      }
      return { precise: Infinity, precise_currency: 'DKK' } as ICostPrice;
    },
  }));

export const SaleItemStore = modelWithCRUD('sale-items', SaleItem).named(
  'SaleItemStore'
);

export const SaleItemReference = types.reference(SaleItem, {
  get(identifier, parent): any {
    return (getRoot(parent!) as RootModelType).saleItemStore.getItemNaive(
      identifier
    );
  },
  set(value) {
    return value.id;
  },
});

export interface ISaleItem extends Instance<typeof SaleItem> {}
export interface ISaleItemSnapshotIn extends SnapshotIn<typeof SaleItem> {}
export interface ISaleItemSnapshotOut extends SnapshotOut<typeof SaleItem> {}
